import { Box, BoxProps } from '@material-ui/core';
import { Currency, formatMoney, formatNumber } from '@nemuru-eng/js-api-client';
import { generatePath } from 'react-router';
import { Link } from 'react-router-dom';

import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { useCustomTheme } from '@/hooks/useCustomTheme';

import { HorizontalScoreCard } from '../../shared/components/HorizontalScoreCard';
import { MetricVariation } from '../../shared/components/MetricVariation';
import { ScoreCardMetricKey } from '../../shared/types/ScoreCardMetricKey';
import { AgentScoreCardMetrics } from '../types/AgentScoreCardMetrics';
import { MetricsHorizontalProgress } from './MetricsHorizontalProgress';

interface AgentScoreCardProps extends BoxProps {
  currency: Currency;
  metric: AgentScoreCardMetrics;
  selectedMetricsType: ScoreCardMetricKey;
  maxAgentValues: AgentScoreCardMetrics;
}

export const AgentScoreCard = ({
  currency,
  metric,
  selectedMetricsType,
  maxAgentValues,
  ...props
}: AgentScoreCardProps) => {
  const { palette } = useCustomTheme();

  return (
    <>
      <HorizontalScoreCard
        maxHeight={'68px'}
        mb={2}
        whiteSpace={'nowrap'}
        icon={
          <Box
            width={24}
            height={40}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            color={'secondary.main'}
            position={'relative'}
          >
            <Font variant="h5">
              <Box
                height={8}
                width={8}
                top={0}
                right={'-4px'}
                position={'absolute'}
                borderRadius={8}
                bgcolor={metric.isAgentBlocked ? palette.error.main : palette.success.main}
              />
              <Icon>{'account_circle'}</Icon>
            </Font>
          </Box>
        }
        title={
          <>
            {metric.agentName}
            <Link
              style={{ display: 'inline-flex', marginLeft: 6, position: 'relative', top: '1px' }}
              to={generatePath(DYNAMIC_PAGE_PATHS.AGENT, {
                merchantId: metric.merchantId,
                channelId: metric.channelId,
                agentId: metric.agentId,
              })}
            >
              <Icon size={14}>open_in_new</Icon>
            </Link>
          </>
        }
        subtitle={
          <Text
            tId={'OF_PREV_PERIOD'}
            dynamicDataTexts={[
              selectedMetricsType === ScoreCardMetricKey.totalPrincipalAmount
                ? formatMoney({ value: metric[selectedMetricsType].previous, currency }, 0)
                : formatNumber(metric[selectedMetricsType].previous, 0),
            ]}
          />
        }
        value={
          selectedMetricsType === ScoreCardMetricKey.totalPrincipalAmount
            ? formatMoney({ value: metric[selectedMetricsType].current, currency }, 0)
            : formatNumber(metric[selectedMetricsType].current, 0)
        }
        subValue={<MetricVariation variation={metric[selectedMetricsType].variation} />}
        hideDivider
        {...props}
      >
        <MetricsHorizontalProgress
          metric={metric}
          maxMetricsValues={maxAgentValues}
          selectedMetricsType={selectedMetricsType}
        />
      </HorizontalScoreCard>
    </>
  );
};
